import { Alert, Col, Row } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileExport } from '@fortawesome/free-solid-svg-icons'
import Pages from './cards/Pages'
import Plan from './cards/Plan'
import Modules from './cards/Modules'
import Subscriptions from './cards/Subscriptions'
import { useCallback, useEffect, useState } from 'react'
import { api } from '../../../auth'
import { DashboardData } from './cards/types.d'
import Payments from './cards/Payments'
import PageHeader from '../../layout/page/PageHeader'
import GenerateReportModal from './report/GenerateReportModal'

const DashboardSkeleton = () => (
    <div className="dashboard-skeleton">
        <Row>
            {[0, 1, 2].map((i) => (
                <Col lg={6} xl={4} className="mb-4" key={i}>
                    <div className="skeleton-card">
                        <div className="skeleton-line skeleton-line--short" />
                        <div className="skeleton-line skeleton-line--wide" />
                        <div className="skeleton-line skeleton-line--medium" />
                    </div>
                </Col>
            ))}
        </Row>
        <Row>
            <Col xl={8} className="mt-3">
                <div className="skeleton-card skeleton-card--tall" />
            </Col>
            <Col xl={4} className="mt-3">
                <div className="skeleton-card skeleton-card--tall" />
            </Col>
        </Row>
    </div>
)

const Dashboard = () => {
    const axiosPrivate = api

    const [dashboardData, setDashboardData] = useState<DashboardData>()
    const [showReportModal, setShowReportModal] = useState(false)

    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState('')

    const loadDashboardData = useCallback(async () => {
        setErrMsg('')
        setIsLoading(true)

        try {
            const response = await axiosPrivate
                .get('/dashboard')
                .finally(() => {
                    setIsLoading(false)
                })
            if (response.data?.success === true) {
                setDashboardData(response.data.data.dashboard)
            } else {
                const errors = response.data?.errors
                setErrMsg(
                    Array.isArray(errors) ? errors.join('<br />') : 'Failed to load dashboard'
                )
            }
        } catch (err: unknown) {
            const axiosErr = err as { response?: { data?: { errors?: string[] }; status?: number } }
            if (Array.isArray(axiosErr.response?.data?.errors)) {
                setErrMsg(axiosErr.response.data.errors.join('<br />'))
            } else if (!axiosErr.response) {
                setErrMsg('No Server Response')
            } else if (axiosErr.response?.status === 404) {
                setErrMsg('Record was not found')
            } else {
                setErrMsg('Failed to load dashboard')
            }
        }
    }, [axiosPrivate])

    useEffect(() => {
        loadDashboardData()
    }, [loadDashboardData])

    return (
        <div className="dashboard">
            <PageHeader
                header="Dashboard"
                icon="gauge-high"
                subtitle="Overview of your site activity and resources"
            />
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    {errMsg}
                </Alert>
            )}
            {isLoading ? (
                <DashboardSkeleton />
            ) : dashboardData ? (
                <div className="dashboard-content">
                    <Row>
                        <Col xs={12} lg={6} xl={3} className="mb-4">
                            <button
                                type="button"
                                className="stat-card stat-card--action"
                                onClick={() => setShowReportModal(true)}
                            >
                                <div
                                    className="stat-card__accent"
                                    style={{
                                        background:
                                            'linear-gradient(90deg, #6366f1, #8b5cf6)',
                                    }}
                                />
                                <div className="stat-card__header stat-card__header--inline">
                                    <div>
                                        <div className="stat-card__label stat-card__label--report">
                                            Report
                                        </div>
                                        <div className="stat-card__value stat-card__value--report">
                                            Generate System Report
                                        </div>
                                        <span className="stat-card__desc">
                                            PDF &amp; CSV — plan, payments,
                                            subscriptions
                                        </span>
                                    </div>
                                    <div
                                        className="stat-card__icon"
                                        style={{
                                            background:
                                                'linear-gradient(135deg, #6366f1, #7c3aed)',
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faFileExport}
                                        />
                                    </div>
                                </div>
                            </button>
                        </Col>
                        {dashboardData.plan && (
                            <Col xs={12} lg={6} xl={3} className="mb-4">
                                <Plan widgetData={dashboardData.plan} />
                            </Col>
                        )}
                        {dashboardData.modules && (
                            <Col xs={12} lg={6} xl={3} className="mb-4">
                                <Modules widgetData={dashboardData.modules} />
                            </Col>
                        )}
                        {dashboardData.pages && (
                            <Col xs={12} lg={6} xl={3} className="mb-4">
                                <Pages widgetData={dashboardData.pages} />
                            </Col>
                        )}
                    </Row>
                    <Row>
                        <Col xl={8} className="mt-3">
                            <Payments />
                        </Col>
                        <Col xl={4} className="mt-3">
                            <Subscriptions />
                        </Col>
                    </Row>
                </div>
            ) : null}

            <GenerateReportModal
                show={showReportModal}
                onHide={() => setShowReportModal(false)}
            />
        </div>
    )
}

export default Dashboard
